import type { ReceiptPayload } from "./types";

const MAX_RECEIPT_BYTES = 460_000;
const MAX_RECEIPT_EDGE = 1_600;

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || "").split(",")[1] || "");
    reader.onerror = () => reject(reader.error ?? new Error("No se pudo leer el comprobante."));
    reader.readAsDataURL(blob);
  });
}

function canvasBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => blob ? resolve(blob) : reject(new Error("No se pudo comprimir el comprobante.")),
      "image/jpeg",
      quality,
    );
  });
}

export async function prepareReceipt(file: File): Promise<ReceiptPayload> {
  if (!file.type.startsWith("image/")) throw new Error("El comprobante debe ser una imagen.");

  let output: Blob = file;
  let mimeType = file.type || "image/jpeg";

  if (file.size > MAX_RECEIPT_BYTES || !["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
    let bitmap: ImageBitmap;
    try {
      bitmap = await createImageBitmap(file);
    } catch {
      throw new Error("No pude preparar esta imagen. Elige una foto JPG, PNG o WebP.");
    }

    const scale = Math.min(1, MAX_RECEIPT_EDGE / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const context = canvas.getContext("2d");
    if (!context) throw new Error("No se pudo preparar el comprobante.");
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();

    for (const quality of [0.82, 0.72, 0.62, 0.52]) {
      output = await canvasBlob(canvas, quality);
      if (output.size <= MAX_RECEIPT_BYTES) break;
    }
    mimeType = "image/jpeg";
  }

  if (output.size > MAX_RECEIPT_BYTES) {
    throw new Error("El comprobante sigue siendo demasiado grande. Elige una imagen más ligera.");
  }

  return {
    name: `${file.name.replace(/\.[^.]+$/, "") || "comprobante"}${mimeType === "image/jpeg" ? ".jpg" : mimeType === "image/png" ? ".png" : ".webp"}`,
    mimeType,
    base64: await blobToBase64(output),
  };
}
